import { hasuraGql } from '@/services/hasura-client';
import { getAppClientData, getDiscovery } from '@/services/app-client.client';
import { appClientApiPath } from '@/utils/constants';
import {
  getActiveEnvUrl,
  removeActiveEnvUrl,
} from '@/core/env-and-content-src/env-url-storage';
import {
  getEnvDetails,
  getEnvDetailsFromAppClientData,
} from '@/core/env-and-content-src/utils';
import {
  type ContentSrc,
  type Env,
} from '@/core/env-and-content-src/env-and-content-src.types';
import { appApiGql, KsAppApiGqlClientError } from '@/services/app-api-client';
import type { SiteMeta } from '@knapsack/hasura-gql-client';
import { ddRum } from '@/services/datadog';
import { isObject, isUrl, makeShortId } from '@knapsack/utils';
import urlJoin from 'url-join';
import {
  isKsAppClientDataAndMeta,
  type KsAppClientData,
} from '@knapsack/types';
import type { SiteLoadResults } from '../../xstate/machines/app/sub-states/site.xstate-types';
import { enablePrototyping } from './enable-prototyping';

type SiteLoad = Promise<SiteLoadResults>;

export type SiteLoadErrorInfo =
  | { type: 'siteId-not-found'; siteId: string }
  | {
      type: 'outdated-app-client';
      currentVersion: string;
      requiredVersion: string;
    }
  | {
      type: 'app-client-connection-failed';
      appClientUrl: string;
    }
  | {
      type: 'app-client-data-fetch-failed';
    }
  | {
      type: 'app-client-deployments-not-set-up';
    }
  | {
      type: 'private-site.user-not-logged-in';
      siteId: string;
    }
  | {
      type: 'private-site.user-not-allowed-to-view-site';
      siteId: string;
    }
  | { type: 'unknown'; message: string };

export class SiteLoadError extends Error {
  info: SiteLoadErrorInfo;
  constructor(info: SiteLoadErrorInfo) {
    switch (info.type) {
      case 'siteId-not-found':
        super(`Site ID "${info.siteId}" not found`);
        break;
      case 'outdated-app-client':
        super(`App Client is outdated`);
        break;
      case 'app-client-connection-failed':
        super(
          `App Client connection failed, please ensure this url is accessible: ${info.appClientUrl}`,
        );
        break;
      case 'unknown':
        super(info.message);
        break;
      case 'app-client-deployments-not-set-up':
        super('Missing Production Environment URL.');
        break;
      case 'private-site.user-not-logged-in':
        super('This is a private site. You must be logged in.');
        break;
      case 'private-site.user-not-allowed-to-view-site':
        super(
          'This workspace is private and you do not have permission to view it.',
        );
        break;
      case 'app-client-data-fetch-failed':
        super('Unable to Connect to Your Server');
        break;
      default: {
        // `never` uses TS to ensure we handle all cases in the switch
        const _exhaustiveCheck: never = info;
        super(`SiteLoadError`);
        break;
      }
    }
    this.name = 'SiteLoadError';
    this.info = info;
  }
}

// Site running local
async function loadSiteFromClientUrl({
  appClientUrl,
}: {
  appClientUrl: string;
}): SiteLoad {
  try {
    const _appClientApiEndpoint = new URL(
      urlJoin(appClientUrl, appClientApiPath),
    ).toString();
  } catch (error) {
    throw new SiteLoadError({
      type: 'unknown',
      message: `This is not a proper url: "${appClientUrl}"`,
    });
  }
  // `getDiscovery` is async, we kick it off now and `await` it later so things can run in parallel
  const discoveryResultPromise = getDiscovery({ appClientUrl });
  const { metaState, ...appClientData } = await getAppClientData({
    appClientUrl,
  }).catch((error) => {
    console.error(`Error loading App Client Data`, error);
    throw new SiteLoadError({
      type: 'app-client-connection-failed',
      appClientUrl,
    });
  });

  const siteIdFromConfig = metaState.meta.knapsackCloudSiteId;
  const info = await hasuraGql.GetSiteBasicInfo({
    siteId: siteIdFromConfig,
  });
  if (!info?.site?.id) {
    throw new SiteLoadError({
      type: 'siteId-not-found',
      siteId: siteIdFromConfig,
    });
  }
  const {
    gitProvider,
    planId,
    status,
    isPrivate,
    latestUrl: prodEnvUrl,
    orgId,
    title,
    repoUrl,
    id: siteId,
  } = info.site;

  const discoveryResult = await discoveryResultPromise;
  const discovery =
    discoveryResult.type === 'success' ? discoveryResult.data : null;

  const env = getEnvDetailsFromAppClientData({
    appClientData: {
      ...appClientData,
      metaState,
    },
    discovery,
    envUrl: appClientUrl,
    prodEnvUrl,
  });
  if (env instanceof Error) {
    throw new SiteLoadError({
      type: 'unknown',
      message: `Error getting env details: ${env.message}`,
    });
  }

  const contentSrc: ContentSrc = {
    type: 'current-env-server',
  };
  const meta: SiteMeta = {
    siteId,
    isPrivate,
    repoUrl,
    title,
    gitProviderType: gitProvider,
    status,
    plan: planId,
    orgId,
    prodEnvUrl,
    // hardcoding to `false` for now to satisfy types - proper implementation will be in a follow up PR
    isKsContentStoredInDb: false,
  };
  const { convertTokenGroupToData, getTokenCollectionsCss } = await import(
    '@knapsack/design-token-utils'
  );
  const tokenData = convertTokenGroupToData(appClientData.tokensSrc);
  const tokenStyles = getTokenCollectionsCss({
    tokenData,
    minimize: true,
  });

  /**
   * Automatically enable prototyping for L40 sites
   */
  if (
    // only when running locally
    env.type === 'development' &&
    // only when running `knapsack start`
    env.appClientMeta.mode === 'start' &&
    // only when on 40 Liter Plan
    planId === 'L40'
  ) {
    enablePrototyping({
      patternsState: appClientData.patternsState,
      settings: appClientData.db.settings.prototyping,
    });
  }
  return {
    appClientData,
    site: {
      env,
      meta,
      contentSrc,
    },
    tokenData,
    tokenStyles,
  };
}

const getSiteContentAndMeta = async ({
  siteId,
  instanceId,
}: {
  siteId: string;
  instanceId: string;
}) => {
  try {
    return await appApiGql.getSiteContentAndMeta({ instanceId });
  } catch (e) {
    if (e instanceof KsAppApiGqlClientError) {
      if (e.errorCode === 'ks.private-site.user-not-logged-in') {
        throw new SiteLoadError({
          type: 'private-site.user-not-logged-in',
          siteId,
        });
      }
      if (e.errorCode === 'ks.private-site.user-not-allowed-to-view-site') {
        throw new SiteLoadError({
          type: 'private-site.user-not-allowed-to-view-site',
          siteId,
        });
      }
      if (e.errorCode === 'ks.app-client-data-fetch-failed') {
        throw new SiteLoadError({
          type: 'app-client-data-fetch-failed',
        });
      }
      if (e.errorCode === 'ks.app-client-deployments-not-set-up') {
        throw new SiteLoadError({
          type: 'app-client-deployments-not-set-up',
        });
      }
    }
    throw new SiteLoadError({
      type: 'unknown',
      message: e.message,
    });
  }
};

async function getEnv({
  appClientData,
  prodEnvUrl,
  siteId,
}: {
  siteId: string;
  prodEnvUrl: string;
  appClientData: KsAppClientData;
}): Promise<Env> {
  if (!isUrl(prodEnvUrl)) {
    throw new Error(`Invalid production env url: "${prodEnvUrl}".`);
  }

  // null here means no environment url, therefore there is only PROD url
  const activeEnvUrl = getActiveEnvUrl({ siteId });
  if (activeEnvUrl && isUrl(activeEnvUrl)) {
    const result = await getEnvDetails({ envUrl: activeEnvUrl, prodEnvUrl });
    if (result instanceof Error) {
      removeActiveEnvUrl({ siteId });
      console.error(
        `Error getting env details from active env url "${activeEnvUrl}", falling back to production env url. Error message: ${result.message}`,
        { cause: result },
      );
      // note that since we are not returning here, the logic below will run
    } else {
      return result;
    }
  }

  if (!isObject(appClientData)) {
    throw new Error(
      'App Client Data from Production Environment is missing. This is a bug.',
    );
  }

  if (!isKsAppClientDataAndMeta(appClientData)) {
    throw new Error(
      'App Client Data from Production Environment is missing "metaState". ',
    );
  }

  const discoveryResult = await getDiscovery({ appClientUrl: prodEnvUrl });
  const result = getEnvDetailsFromAppClientData({
    appClientData,
    envUrl: prodEnvUrl, // no activeEnvUrl means prod must be envUrl
    prodEnvUrl,
    discovery: discoveryResult.type === 'success' ? discoveryResult.data : null,
  });
  if (result instanceof Error) {
    throw new Error(
      `Error getting Env details from appClientData: ${result.message}`,
      { cause: result },
    );
  }
  return result;
}

export async function loadSiteById({
  siteId,
  instanceId,
}: {
  siteId: string;
  instanceId: string;
}): Promise<SiteLoadResults> {
  if (siteId === 'custom') {
    throw new Error(`Cannot use this function to load siteId "custom".`);
  }

  const {
    site: {
      appClientDataInfo: {
        appClientData,
        instanceStatus,
        gitBranch,
        lastCommittedDataChangeId,
        tokens,
      },
      meta,
    },
  } = await getSiteContentAndMeta({ siteId, instanceId });
  let tokenData = tokens?.tokenData;
  let tokenStyles = tokens?.tokenStyles;
  if (!tokenData && Object.keys(appClientData.tokensSrc).length > 0) {
    console.time('tokens conversion');
    const { convertTokenGroupToData, getTokenCollectionsCss } = await import(
      '@knapsack/design-token-utils'
    );
    tokenData = convertTokenGroupToData(appClientData.tokensSrc);
    tokenStyles = getTokenCollectionsCss({
      tokenData,
      minimize: true,
    });
    console.timeEnd('tokens conversion');
    console.debug(
      `tokens conversion: data converted locally since API returned no data`,
    );
  } else {
    console.debug(`tokens conversion: data returned from API`, {
      fromCache: tokens?.fromCache,
    });
  }

  const {
    orgId,
    prodEnvUrl,
    repoUrl,
    siteId: loadedSiteId,
    title,
    isPrivate,
    gitProviderType,
    plan,
    status,
    isKsContentStoredInDb,
  } = meta;

  if (!loadedSiteId) {
    throw new SiteLoadError({
      type: 'siteId-not-found',
      siteId: loadedSiteId,
    });
  }

  // this logic will need to change when we have content in DB AND have App Client hooked up for rendering (but not content)
  const env = isKsContentStoredInDb
    ? { type: 'none' as const }
    : await getEnv({
        appClientData,
        prodEnvUrl,
        siteId,
      });

  const siteMeta: SiteMeta = {
    siteId,
    title,
    isPrivate,
    repoUrl,
    gitProviderType,
    status,
    plan,
    orgId,
    prodEnvUrl,
    isKsContentStoredInDb,
  };

  // Default to latest for SiteLoadResults, override contentSrc cloud authoring branches
  const defaultContentSrc: ContentSrc = {
    type: 'cloud-authoring',
    instance: {
      type: 'latest',
    },
  };
  const siteLoadResults: SiteLoadResults = {
    appClientData,
    site: {
      contentSrc: defaultContentSrc,
      env,
      meta: siteMeta,
    },
    tokenData,
    tokenStyles,
  };

  switch (instanceId) {
    case 'draft': {
      throw new Error(`InstanceTag "draft" is not yet implemented.`);
    }
    case 'latest': {
      return siteLoadResults;
    }
    default: {
      // We won't have this if instance failed to load
      if (!instanceStatus) {
        return loadSiteById({ siteId, instanceId: 'latest' });
      }

      const cloudAuthoringContentSrc: ContentSrc = {
        type: 'cloud-authoring',
        instance: {
          type: 'branch',
          instanceId,
          instanceStatus,
          lastCommittedDataChangeId,
          gitBranch,
        },
      };
      siteLoadResults.site.contentSrc = cloudAuthoringContentSrc;

      return siteLoadResults;
    }
  }
}

export async function loadSiteFromUrlParams(
  info:
    | {
        type: 'site';
        siteId: string;
        instanceId: string;
      }
    | {
        type: 'custom';
        customUrl: string;
      },
): SiteLoad {
  const timerLabel = `loadSite-${makeShortId()}`;
  console.time(timerLabel);

  try {
    switch (info.type) {
      case 'site': {
        const { siteId, instanceId } = info;
        const ref = ddRum.startDurationVital('load-site');
        const results = await loadSiteById({
          siteId,
          instanceId,
        });
        ddRum.stopDurationVital(ref);
        return results;
      }
      case 'custom': {
        const results = await loadSiteFromClientUrl({
          appClientUrl: info.customUrl,
        });
        return results;
      }
      default: {
        const _exhaustiveCheck: never = info;
        throw new Error(`Unhandled info type: ${JSON.stringify(info)}`);
      }
    }
  } finally {
    console.timeEnd(timerLabel);
  }
}
